import type { KpiReport } from '@/js/types/kpiReport.ts';

export type BudgetEntrySource = {
    emitter: 'viewInvoice' | 'open:revenueEntry' | 'open:PurchaseOrder' | 'budget:entry';
    payload: {
        event: number;
        entry: number;
    };
};

type SendEmailType = 'invoice' | 'purchase order' | 'KPI Report';

export interface EventMap {
    'create:bankTransaction': {
        parent_id: number;
        transactionable_id: number;
    };
    routeChanged: {
        route: string;
        uri: string;
        params?: Record<string, unknown>;
    };
    'newBudgetEntry:open': {
        department?: number;
        category?: number;
    };
    'subcategory:open': {
        department?: number;
        category?: number;
    } | void;
    'create-tickets:open': {
        department?: number;
        category?: number;
    };
    includeEstimated: boolean;
    'reload:entries': void;
    'reload:statistics': void;
    'budget:entry': {
        entry: number;
        event: number;
        scenarioId?: number;
        defaultTab?: string;
        source?: BudgetEntrySource;
    };
    'close:BudgetEntry': void;
    'refresh:BudgetEntry': void;
    'reload:purchaseorders': void;
    'purchase-order:create': {
        department?: number;
        category?: number;
        title?: string;
        supplier?: number;
        entry?: number;
    } | void;
    'supplierInvoice:created': {
        supplierInvoice: number;
        purchaseOrders: number[];
    };
    'supplierInvoice:authorised': void;
    'supplierInvoice:deleted': {
        supplierInvoice: number;
        purchaseOrders: number[];
    };
    'purchaseInvoice:create': Array<Record<string, unknown>>;
    'view:supplier': number;
    'purchaseOrder:view': {
        purchaseOrder: number;
        event?: number;
        source?: Record<string, unknown>;
    };
    forceRefetchEvent: void;
    'salesInvoice:create': {
        entry: number;
    };
    'forceUpdate:profit-overview': void;
    viewInvoice: {
        type: string;
        routeData: {
            event?: number;
            purchaseOrder?: number;
            revenueEntry?: number;
            supplierInvoice?: number;
            customerInvoice?: number;
            openPaymentDates?: boolean;
        };
    };
    'revenue:create': {
        department?: number;
        budgetEntry?: number;
        category?: number;
        supplier?: number;
    } | null;
    'inviteSupplierModal:open': number;
    'open:revenueEntry': {
        event: number;
        entry: number;
    };
    'open:PurchaseOrder': {
        event: number;
        entry: number;
    };
    'revenue:entry': {
        entry: number;
        source?: {
            emitter: 'budget:entry';
            payload: {
                event: number;
                entry: number;
            };
        };
    };
    'reload:payrun': void;
    'refresh:approvals': void;
    'reload:payruns': void;
    'purchaseOrder:reloadLineItems': void;
    pageChanged: void;
    'create:department': void;
    'reload:departments': void;
    createContact: {
        supplierType?: number;
        supplierName?: string;
    } | void;
    'create:supplierInvoice': void;
    'reload:charts': void;
    rechargeBudgetEntry: {
        budgetEntry: {
            id: number;
            event_id: number;
            category_id: number;
            fixed_cost: number;
            budgeted_cost: number;
        };
        type: string;
    };
    currencyConverter: void;
    quickJump: void;
    openOCRImport: void;
    'send:email': {
        model: KpiReport;
        type: SendEmailType;
        file?: File;
    };
    'contactPortalInvite:open': number;
    'audits:open': {
        auditableType: string;
        auditableId: number;
    };
}

import { useLogs } from '@/js/composables/useLogs.ts';

const { log } = useLogs();

class EvtBus {
    private _events: {
        [K in keyof EventMap]?: Array<{
            callback: (payload: EventMap[K]) => void;
            wrapper: (event: Event) => void;
        }>;
    } = {};

    public $emit<K extends keyof EventMap>(name: K, data?: EventMap[K]): void {
        dispatchEvent(new CustomEvent(String(name), { detail: data }));
        log('eventbus', { action: 'emit', name: String(name), payload: data });
    }

    public $on<K extends keyof EventMap>(name: K, callback: (payload: EventMap[K]) => void): void {
        const wrapper = (event: Event) => {
            callback((event as CustomEvent).detail);
        };

        if (!this._events[name]) {
            this._events[name] = [];
        }

        this._events[name].push({
            callback,
            wrapper,
        });

        addEventListener(String(name), wrapper);
        log('eventbus', { action: 'on', name: String(name) });
    }

    public $off<K extends keyof EventMap>(name: K, callback?: (payload: EventMap[K]) => void): void {
        const handlers = this._events[name];

        if (!handlers) {
            return;
        }

        if (callback) {
            const handlerIndex = handlers.findIndex((handler) => handler.callback === callback);
            if (handlerIndex === -1) {
                return;
            }

            const [handler] = handlers.splice(handlerIndex, 1);
            if (handler) {
                removeEventListener(String(name), handler.wrapper);
            }

            if (handlers.length === 0) {
                delete this._events[name];
            }
        } else {
            handlers.forEach((handler) => {
                removeEventListener(String(name), handler.wrapper);
            });
            delete this._events[name];
        }

        log('eventbus', { action: 'off', name: String(name) });
    }
}

export const Eventbus = new EvtBus();
